<template>
  <dd-form-page
      :bread-crumbs="breadCrumbs"
      :header="pageHeader"
      @submit="onSubmit"
  >
    <template #steps>
      <dd-step title="Process">
        <dd-header-card header="Automation instellen">
          <template #action>
            <dd-information-tooltip placement="top">
              Met de automatiseringstool kun je verschillende processen automatiseren.
            </dd-information-tooltip>
          </template>
          <dd-radio-group
              title="Kies uit een van de volgende processen"
              rules="required"
              v-model="processType">
            <automation-type-radio
                value="data_enrichment"
                title="Data verrijking"
                info="Op basis van een selectie in je relatiebestand gaan wij de gegevens verrijken. Denk hierbij aan de ophalen van geschatte woningwaarde, energielabel en perceeloppervlakte."
            />
            <automation-type-radio
                value="campaign"
                title="Campagne"
                info="Maak een campagne aan om een (automatisch) bericht naar je relaties testuren. Dit kan via de mail of via het klantportaal."
            />
            <automation-type-radio
                value="selection"
                title="Standaard selectie"
                info="Maak een selectie in je relatiebestand voor het downloaden van een .csv bestand, handige signaleringen en het maken van automatische taken."
            />
          </dd-radio-group>
        </dd-header-card>
      </dd-step>

      <dd-step title="Selectie">
        <campaign-selection-step
            v-if="createCampaign"
            ref="createCampaignComponent"></campaign-selection-step>
        <data-enrichment-selection-step
            v-if="createDataEnrichment"
            ref="createDataEnrichmentComponent"></data-enrichment-selection-step>
        <selection-selection-step
            v-if="createSelection"
            ref="createSelectionComponent"></selection-selection-step>
      </dd-step>
      <dd-step title="Instellingen">
        <campaign-settings-step
            v-if="createCampaign"
            ref="createCampaignComponent"></campaign-settings-step>
        <data-enrichment-settings-step
            v-if="createDataEnrichment"
            ref="createDataEnrichmentComponent"></data-enrichment-settings-step>
        <selection-settings-step
            v-if="createSelection"
            ref="createSelectionComponent">
        </selection-settings-step>
      </dd-step>

      <dd-step title="Actie instellingen">
        <dd-header-card v-if="send_email || createCampaign" header="E-mail versturen instellingen" class="mb-md">
          <template #icon>
            <EmailIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureEmailSettingsComponent v-model="creatingAutomationModel"/>
        </dd-header-card>

        <dd-header-card v-if="create_task" header="Taak aanmaken instellingen" class="mb-md">
          <template #icon>
            <CheckboxOutlineIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureTaskSettingsComponent v-model="creatingAutomationModel" class="mt-md"/>
        </dd-header-card>

        <dd-header-card v-if="create_signal" header="Signaal aanmaken instellingen" class="mb-md">
          <template #icon>
            <BellIcon class="icon-size-lg fill-primary"/>
          </template>
          <ConfigureSignalSettingsComponent v-model="creatingAutomationModel" class="mt-md"/>
        </dd-header-card>

        <dd-header-card v-if="!(send_email || createCampaign) && !create_task && !create_signal"
                        header="Geen actie instellingen" class="mb-md">
          <template #icon>
            <InformationOutlineIcon class="icon-size-lg fill-primary"/>
          </template>
          Er zijn geen acties geselecteerd om te automatiseren.
        </dd-header-card>
      </dd-step>

      <dd-step title="Overzicht">
        <ShowAutomationComponent v-bind:automation="creatingAutomation" v-bind:is-creating="true"/>
      </dd-step>
    </template>
  </dd-form-page>
</template>

<script>
import store from '../store'
import automationApi from '~/api/automation'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import AutomationTypeRadio from '../components/AutomationTypeRadio.vue'
import CampaignSelectionStep from '../components/campaign/CampaignSelectionStep.vue'
import CampaignSettingsStep from '../components/campaign/CampaignSettingsStep.vue'
import DataEnrichmentSelectionStep from '../components/data_enrichment/DataEnrichmentSelectionStep.vue'
import DataEnrichmentSettingsStep from '../components/data_enrichment/DataEnrichmentSettingsStep.vue'
import ConfigureTaskSettingsComponent from '../components/ConfigureTaskSettingsComponent.vue'
import ConfigureSignalSettingsComponent from '../components/ConfigureSignalSettingsComponent.vue'
import ConfigureEmailSettingsComponent from '../components/ConfigureEmailSettingsComponent.vue'
import ShowAutomationComponent from '../components/show/ShowAutomationComponent.vue'
import SelectionSelectionStep from '../components/selection/SelectionSelectionStep.vue'
import SelectionSettingsStep from '../components/selection/SelectionSettingsStep.vue'
import automationShowStore from "~/pages/automation/components/show/store";

export default {
  name: 'CreateOrEditAutomationComponent',
  components: {
    ShowAutomationComponent,
    ConfigureTaskSettingsComponent,
    ConfigureSignalSettingsComponent,
    ConfigureEmailSettingsComponent,
    CampaignSelectionStep,
    CampaignSettingsStep,
    DataEnrichmentSelectionStep,
    DataEnrichmentSettingsStep,
    SelectionSelectionStep,
    SelectionSettingsStep,
    AutomationTypeRadio,
  },
  data () {
    return {
      loaded: false,
      loading: false,
      breadCrumbs: [
        {
          name: 'Automations',
          to: () => this.navigator.navigatePath(this.route('vue.dashboard.automations.index'))
        },
      ]
    }
  },
  watch: {
    creatingAutomation: {
      deep: true,
      handler () {
        this.setFormDirty(true)
      }
    }
  },
  beforeCreate () {
    const STORE_KEY = '$_automation'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
    const AUTOMATION_SHOW_STORE_KEY = '$_automation_show'
    // eslint-disable-next-line no-underscore-dangle
    if (!(AUTOMATION_SHOW_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(AUTOMATION_SHOW_STORE_KEY, automationShowStore)
    }

    this.loaded = true
  },
  beforeRouteLeave (to, from, next) {
    if (!this.formDirty) {
      next()
      return
    }
    this.$confirm({
      title: "Weet je het zeker?",
      content: "Je hebt wijzigingen aangebracht die niet zijn opgeslagen.",
      okText: "Niet opslaan",
      cancelText: "Verder bewerken",
      onOk: () => {
        this.resetCreatingAutomation()
        this.resetFormPageErrors()
        next()
      },
      onCancel: () => {}
    })
  },
  activated () {
    // if editing
    if (this.automationId) {
      if (!this.selectedAutomation || this.selectedAutomation.id !== this.automationId) {
        this.loading = true

        automationApi.getById(this.automationId)
          .then((response) => {
            this.selectAutomation(response.data);
            this.setCreatingAutomation(response.data);
            this.loading = false;
            this.$set(this.breadCrumbs, 1, {
              name: response.data.title,
              to: () => this.navigator.navigatePath(this.route("vue.dashboard.automations.show", { automation: response.data.id }))
            });
          })
      } else {
        this.setCreatingAutomation(this.selectedAutomation)
      }
    }
  },
  computed: {
    ...mapGetters('$_automation', {
      isNextStepEnabled: 'isNextStepEnabled',
      creatingAutomation: 'creatingAutomation',
      selectedAutomation: 'selectedAutomation',
    }),
    ...mapState(`$_automation`, {
      send_email: (state) => state.creatingAutomation.send_email,
      create_task: (state) => state.creatingAutomation.create_task,
      create_signal: (state) => state.creatingAutomation.create_signal,
      formDirty: (state) => state.formDirty,
    }),
    pageHeader () {
      if (this.loading) {
        return 'Laden...'
      } else if (this.automationId) {
        return 'Automation ' + this.creatingAutomation?.title + ' bewerken'
      } else {
        return 'Automation aanmaken'
      }
    },
    processType: {
      get () {
        return this.$store.state.$_automation.creatingAutomation?.type || null;
      },
      set (value) {
        let creatingAutomation = Object.assign({}, this.$store.state.$_automation.creatingAutomation, { type: value })

        this.$store.commit('$_automation/SET_CREATING_AUTOMATION', creatingAutomation)
      },
    },
    creatingAutomationModel: {
      get () {
        return this.creatingAutomation
      },
      set (formConfig) {
        this.setCreatingAutomation(formConfig)
      }
    },
    // edit page for global automation different from the edit page for automation
    automationId () {
      return this.$route.params?.automation
    },
    isEditing () {
      return this.automationId
    },
    createDataEnrichment () {
      return this.processType === 'data_enrichment'
    },
    createCampaign () {
      return this.processType === 'campaign'
    },
    createSelection () {
      return this.processType === 'selection'
    },
  },
  methods: {
    ...mapActions('$_automation', [
      'selectAutomation',
      'storeAutomation',
      'updateAutomation',
    ]),
    ...mapMutations('$_automation', {
      setCreatingAutomation: 'SET_CREATING_AUTOMATION',
      resetCreatingAutomation: 'RESET_CREATING_AUTOMATION_TO_DEFAULT',
      setFormDirty: 'SET_FORM_DIRTY',
    }),
    ...mapMutations('$_form_page', {
      resetFormPageErrors: 'RESET_AXIOS_ERROR',
    }),
    onSuccess (response) {
      this.$store.commit('$_form_page/SET_SUCCESS_RESPONSE', response, { root: true })
      this.$store.commit('$_automation/RESET_CREATING_AUTOMATION_TO_DEFAULT')
      this.setFormDirty(false)
      this.navigator.navigatePath(this.route('vue.dashboard.automations.index'))
    },
    onError (error) {
      this.$store.commit('$_form_page/SET_AXIOS_ERROR', error, { root: true })
    },
    onSubmit () {
      if (this.isEditing) {
        this.updateAutomation(this.creatingAutomation)
            .then((response) => {
              this.onSuccess(response)
            })
            .catch((error) => {
              this.onError(error)
            })
      } else {
        this.storeAutomation(this.creatingAutomation)
            .then((response) => {
              this.onSuccess(response)
            })
            .catch((error) => {
              this.onError(error)
            })
      }
    },
    onCancel () {
      this.$store.commit('$_automation/RESET_CREATING_AUTOMATION_TO_DEFAULT')
      this.$emit('go-to', 'automation_overview')
    }
  },
}
</script>

<style scoped>
.automation-create-component {
  position: relative;
}
</style>
